/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React from "react";
import { Box, useTheme, Modal, Fade, Backdrop, Typography, Stack, Divider, Tooltip, IconButton } from "@mui/material";
import { InventoryTransfer, ModalBase, Nullable } from "../../utils/Interfaces";
import AttachmentManager from "../../components/general/AttachmentManager";
import { ModelName } from "../../utils/Requests";
import Chip from "@mui/material/Chip";
import { lookupTransferType, lookupTransferTypeColor } from "../../utils/Constants";
import { ArrowForward } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

export interface InventoryTransferDetailsModalProps extends ModalBase {
    transfer: Nullable<InventoryTransfer>;
}

const InventoryTransferDetailsModal = (props: InventoryTransferDetailsModalProps) => {
    const { open, handleClose, transfer } = props;
    const theme = useTheme();

    if (transfer === null) {
        return <Box />;
    }

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                            width: 600,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h5" textAlign="center" sx={{ mb: 4 }}>
                                Transfer Details
                            </Typography>
                            <Box sx={{ mr: -2, mt: -1 }}>
                                <Tooltip title="Close">
                                    <IconButton onClick={() => handleClose()} size="large">
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Transfer Type</Typography>
                            <Chip
                                color={lookupTransferTypeColor(transfer?.transfer_type)}
                                label={lookupTransferType(transfer?.transfer_type)}
                                style={{ fontWeight: "bold" }}
                            />
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Transfer</Typography>
                            <Stack direction="row" justifyContent="flex-end">
                                <Typography color={transfer.from_location?.name === null ? "info.main" : "inherit"}>
                                    {transfer.from_location?.name ?? "IN"}&nbsp;&nbsp;
                                </Typography>
                                <ArrowForward fontSize="small" />
                                <Typography color={transfer.to_location?.name === null ? "info.main" : "inherit"}>
                                    &nbsp;&nbsp;{transfer.to_location?.name ?? "OUT"}
                                </Typography>
                            </Stack>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Part Name</Typography>
                            <Typography fontWeight="bold">{transfer?.material.name ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Manufacturer</Typography>
                            <Typography fontWeight="bold">{transfer?.material.manufacturer ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Part #</Typography>
                            <Typography fontWeight="bold">{transfer?.material.part_number ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Unit Price</Typography>
                            <Typography fontWeight="bold">${transfer?.material.price ?? "--"}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>User</Typography>
                            <Typography fontWeight="bold">
                                {transfer?.user.first_name} {transfer?.user.last_name}
                            </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography>Timestamp</Typography>
                            <Typography fontWeight="bold">
                                {new Date(transfer?.timestamp).toLocaleDateString()}&nbsp;
                                {new Date(transfer?.timestamp).toLocaleTimeString()}
                            </Typography>
                        </Stack>

                        {(transfer?.notes ?? "") !== "" && (
                            <Box>
                                <Divider sx={{ mt: 3, mb: 2 }}>Notes</Divider>
                                <Typography>{transfer?.notes}</Typography>
                            </Box>
                        )}

                        <Divider sx={{ mt: 3, mb: 2 }}>Attachments</Divider>

                        <AttachmentManager
                            model={ModelName.InventoryTransfer}
                            idKey={transfer?.id ?? -1}
                            editing={true}
                            readOnly={false}
                        />
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default InventoryTransferDetailsModal;
